/**
 * stolenVehiclesAdmin.tsx — Admin UI for the Stolen Vehicles database.
 *
 * Single add form + bulk upload (paste a JSON array, or upload a
 * .json file). Edit/delete via inline actions on each row.
 *
 * v0.4.9.2.3c22a (admin form polish):
 *   - Country field is now a strict alpha-3 SearchableSelect sourced from
 *     `src/data/countries.ts`. Replaces the free-text Input + `toUpperCase()`
 *     that used to accept anything.
 *   - Country pre-fills with the admin's org country on mount.
 *   - Edit modal also uses the dropdown (pre-fills from the record's
 *     current country).
 *   - List-row icons (Pencil + Trash) were CROOKED — Pencil and Trash were
 *     rendered as siblings of the title block under `justify-between`, so
 *     they ended up in the middle / right with no gap. Now wrapped in a
 *     `<div className="flex items-center gap-1">` so they sit together at
 *     the right edge with a proper gap, matching Most Wanted / Missing
 *     Persons.
 *   - Pencil edit button switched to blue (matches Most Wanted c22a polish).
 *
 * v0.4.9.2.3c22b (reference image upload):
 *   - Single-form admin uploads compressed to 480 px JPEG q=0.8 in
 *     `src/utils/imageCompress.ts` BEFORE hitting the API. The server
 *     stores the file under /var/www/uploads/stolen-vehicles/ and saves
 *     the public URL in the new `stolen_vehicles.photo_url` column.
 *   - Bulk upload remains METADATA-ONLY (no per-row images).
 *   - Edit modal lets you REPLACE the existing photo. If you don't pick a
 *     new image, the server keeps the existing one (omit `image` from
 *     the PATCH body). This is different from Most Wanted — there is no
 *     separate face gallery for vehicles, so swap is from the modal.
 *   - List rows show a 48 × 48 thumbnail on the left when present.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type BoloTargetType } from '../services/api';
import { useTranslation, Trans } from 'react-i18next';
import { FiltersBar, applyFilters, type DateRange } from '../components/FiltersBar';
import { ArrowLeft, Plus, Trash2, X, Upload, FileUp, Pencil, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { BoloTab } from '../components/BoloTab';
import { SearchableSelect, countryListToOptions } from '../components/SearchableSelect';
import { COUNTRIES } from '../data/countries';
import { useUserOrgCountry } from '../hooks/useUserOrgCountry';
import { compressImage } from '../utils/imageCompress';

const TAB_TARGET_TYPE: BoloTargetType = 'stolen_vehicle';

export default function StolenVehiclesAdmin() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  // v0.4.9.2.2: BOLO tab
  const [activeTab, setActiveTab] = useState<'records' | 'bolo'>('records');
  const [availableOrgs, setAvailableOrgs] = useState<Array<{ id: string; name: string; country: string }>>([]);
  const isAgentAdmin = user?.role === 'agentAdmin' || user?.role === 'superadmin';
  const currentOrgId = user?.civil_respondent_id ?? null;
  // v0.4.9.2.3c22a: pre-fill country with admin's org country on mount.
  const orgCountry = useUserOrgCountry();
  const bulkFileRef = useRef<HTMLInputElement | null>(null);
  // v0.4.9.2.3c22b: hidden file inputs for the image uploads in Add + Edit.
  const imageFileRef = useRef<HTMLInputElement | null>(null);
  const editImageFileRef = useRef<HTMLInputElement | null>(null);
  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  // v0.4.9.2.3c22ca: search + date-range filter on the records tab.
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const end = new Date(); end.setHours(23, 59, 59, 999);
    const start = new Date(); start.setDate(start.getDate() - 6); start.setHours(0, 0, 0, 0);
    return { start, end };
  });
  const [showBulk, setShowBulk] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Single add form
  const [form, setForm] = useState({
    license_plate: '', country: '', make: '', model: '', color: '',
    year: '', reporting_station: '',
  });
  const [submitting, setSubmitting] = useState(false);
  // v0.4.9.2.3c22b: compressed image data URL + preview for the Add form.
  const [imageFile, setImageFile] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageCompressing, setImageCompressing] = useState(false);

  // v0.4.9.2.3c22a: pre-fill the form's country with the admin's org country.
  useEffect(() => {
    if (orgCountry && !form.country) {
      setForm(f => ({ ...f, country: orgCountry }));
    }
  }, [orgCountry]);

  // Edit state
  const [editing, setEditing] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({
    license_plate: '', country: '', make: '', model: '', color: '',
    year: '', reporting_station: '', status: 'active',
  });
  const [editSubmitting, setEditSubmitting] = useState(false);
  // v0.4.9.2.3c22b: edit-image state. `editImagePreview` is what the modal
  // DISPLAYS (existing photo_url OR the new compressed data URL the user
  // just picked). `editImageFile` is only set when the user actually picked
  // a new image, and is what gets sent to the API on save.
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);
  const [editImageFile, setEditImageFile] = useState<string | null>(null);
  const [editImageCompressing, setEditImageCompressing] = useState(false);

  // Bulk
  const [bulkText, setBulkText] = useState('');
  const [bulkResult, setBulkResult] = useState<{ inserted: number; failed: number; errors: any[] } | null>(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  function load() {
    setLoading(true);
    api.listStolenVehicles()
      .then(setList)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  // v0.4.9.2.3c22ca: derive filtered record list from search + date range.
  // Searches across license_plate, make, model, color, country, reporting_station.
  const filteredList = applyFilters(
    list,
    search,
    dateRange,
    (v: any) => v.created_at,
    (v: any) =>
      [
        v.license_plate,
        v.make,
        v.model,
        v.color,
        v.country,
        v.reporting_station,
        v.year?.toString?.(),
      ]
        .filter(Boolean)
        .join(' ')
  );

  function resetForm() {
    setForm({ license_plate: '', country: '', make: '', model: '', color: '',
              year: '', reporting_station: '' });
    setImageFile(null);
    setImagePreview(null);
    setError(null);
  }

  // v0.4.9.2.3c22b: handle image pick in the Add form. Compresses client-side
  // (480 px max-dim, JPEG q=0.8) before storing the data URL in state.
  async function onImageFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageCompressing(true);
    try {
      const dataUrl = await compressImage(file);
      setImageFile(dataUrl);
      setImagePreview(dataUrl);
    } catch (err: any) {
      alert(t('sv.err.processImage') + ': ' + (err?.message || t('common.unknown')));
    } finally {
      setImageCompressing(false);
      if (imageFileRef.current) imageFileRef.current.value = '';
    }
  }

  // v0.4.9.2.3c22b: handle image pick in the Edit modal. The preview shows
  // the new data URL immediately; the original photo_url is replaced only on save.
  async function onEditImageFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditImageCompressing(true);
    try {
      const dataUrl = await compressImage(file);
      setEditImageFile(dataUrl);
      setEditImagePreview(dataUrl);
    } catch (err: any) {
      alert(t('sv.err.processImage') + ': ' + (err?.message || t('common.unknown')));
    } finally {
      setEditImageCompressing(false);
      if (editImageFileRef.current) editImageFileRef.current.value = '';
    }
  }

  async function submit() {
    if (!form.license_plate || !form.country) {
      setError(t('sv.err.plateCountryRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.createStolenVehicle({
        license_plate: form.license_plate,
        country: form.country,
        make: form.make || undefined,
        model: form.model || undefined,
        color: form.color || undefined,
        year: form.year ? parseInt(form.year) : undefined,
        reporting_station: form.reporting_station || undefined,
      }, imageFile ?? undefined);
      setShowAdd(false);
      resetForm();
      load();
    } catch (err: any) {
      setError(err.message || t('records.err.createFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitBulk() {
    setBulkSubmitting(true);
    setBulkResult(null);
    try {
      const vehicles = JSON.parse(bulkText);
      if (!Array.isArray(vehicles)) throw new Error('Top-level must be an array');
      const r = await api.bulkCreateStolenVehicles(vehicles);
      setBulkResult(r);
      load();
    } catch (err: any) {
      setError(t('sv.err.bulkFailed') + ': ' + err.message);
    } finally {
      setBulkSubmitting(false);
    }
  }

  function onBulkFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setBulkText(reader.result as string);
    reader.readAsText(file);
  }

  async function deleteVehicle(id: string) {
    if (!confirm(t('sv.confirmDeleteEntry'))) return;
    try {
      await api.deleteStolenVehicle(id);
      load();
    } catch (err: any) {
      alert(t('common.errorWithColon') + ' ' + err.message);
    }
  }

  function startEdit(v: any) {
    setEditing(v);
    setEditForm({
      license_plate: v.license_plate || '',
      country: v.country || '',
      make: v.make || '',
      model: v.model || '',
      color: v.color || '',
      year: v.year != null ? String(v.year) : '',
      reporting_station: v.reporting_station || '',
      status: v.status || 'active',
    });
    // v0.4.9.2.3c22b: show existing photo in the modal until the user
    // picks a new one (then preview swaps to the new data URL).
    setEditImagePreview(v.photo_url ?? null);
    setEditImageFile(null);
  }

  function closeEdit() {
    setEditing(null);
    setEditImagePreview(null);
    setEditImageFile(null);
  }

  async function submitEdit() {
    if (!editing) return;
    if (!editForm.license_plate || !editForm.country) {
      alert(t('sv.err.plateCountryRequired'));
      return;
    }
    setEditSubmitting(true);
    try {
      await api.updateStolenVehicle(editing.id, {
        license_plate: editForm.license_plate,
        country: editForm.country,
        make: editForm.make || undefined,
        model: editForm.model || undefined,
        color: editForm.color || undefined,
        year: editForm.year ? parseInt(editForm.year) : undefined,
        reporting_station: editForm.reporting_station || undefined,
        status: editForm.status,
      }, editImageFile ?? undefined);
      closeEdit();
      load();
    } catch (err: any) {
      alert(t('common.errorWithColon') + ' ' + err.message);
    } finally {
      setEditSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="px-4 py-3 bg-card border-b border-border flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/civil-respondent')} className="text-muted-foreground">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-semibold text-foreground">{t('civil.stolenVehicles.title')}</h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setShowBulk(s => !s); setShowAdd(false); }}
            className="flex items-center gap-1 px-3 py-1.5 border border-input rounded-lg text-sm hover:bg-background"
          >
            <FileUp className="w-4 h-4" /> {t('sv.bulk.loadFromFile')}
          </button>
          <button
            onClick={() => { setShowAdd(s => !s); setShowBulk(false); }}
            className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90"
          >
            {showAdd ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showAdd ? t('common.cancel') : t('common.add')}
          </button>
        </div>
      </header>

      <input ref={bulkFileRef} type="file" accept=".json" onChange={onBulkFile} className="hidden" />

      <main className="max-w-3xl mx-auto p-4">
        {isAgentAdmin && (
          <div className="border-b border-border flex gap-1 mb-4">
            <button
              type="button"
              onClick={() => setActiveTab('records')}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                activeTab === 'records'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('records.tabRecords')}
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('bolo');
                if (user?.role === 'superadmin' && availableOrgs.length === 0) {
                  api.listCivilRespondents().then((orgs: any[]) => {
                    setAvailableOrgs(
                      orgs.map((o) => ({
                        id: o.id,
                        name: o.division_name || o.name || o.id,
                        country: o.country || '',
                      }))
                    );
                  }).catch(() => {});
                }
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                activeTab === 'bolo'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('records.tabBolo')}
            </button>
          </div>
        )}

        {activeTab === 'records' && showAdd && (
          <div className="bg-card border border-border rounded-xl p-5 shadow-sm mb-4">
            <h2 className="font-semibold text-foreground mb-4">{t('civil.stolenVehicles.newTitle')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label={t('sv.field.plate')} value={form.license_plate} onChange={v => setForm({ ...form, license_plate: v.toUpperCase() })} />
              {/* v0.4.9.2.3c22a: strict alpha-3 country dropdown (pre-filled with org country). */}
              <SearchableSelect
                label={t('records.field.country')}
                value={form.country}
                onChange={v => setForm({ ...form, country: v })}
                options={countryListToOptions(COUNTRIES)}
                placeholder={t('records.field.searchCountry')}
                required
              />
              <Input label={t('sv.field.make')} value={form.make} onChange={v => setForm({ ...form, make: v })} />
              <Input label={t('sv.field.model')} value={form.model} onChange={v => setForm({ ...form, model: v })} />
              <Input label={t('sv.field.color')} value={form.color} onChange={v => setForm({ ...form, color: v })} />
              <Input label={t('sv.field.year')} type="number" value={form.year} onChange={v => setForm({ ...form, year: v })} />
              <Input label={t('records.field.reportingStation')} value={form.reporting_station} onChange={v => setForm({ ...form, reporting_station: v })} className="md:col-span-2" />
            </div>

            {/* v0.4.9.2.3c22b: reference image upload (compressed to 480 px JPEG q=0.8 client-side).
                Optional but recommended — gives LE agents a real photo of the vehicle. */}
            <div className="mt-4">
              <label className="text-xs text-muted-foreground uppercase tracking-wide">
                {t('sv.image.optional')}
              </label>
              <div className="mt-2 flex items-center gap-3">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt={t('sv.alt.vehiclePreview')}
                    className="w-20 h-20 object-cover rounded border border-border"
                  />
                ) : (
                  <div className="w-20 h-20 bg-accent rounded flex items-center justify-center text-xs text-muted-foreground">
                    {t('records.face.noImage')}
                  </div>
                )}
                <label className="cursor-pointer px-3 py-2 border border-input rounded text-sm hover:bg-background">
                  {imageFile ? t('records.face.change') : t('records.face.chooseFile')}
                  <input
                    ref={imageFileRef}
                    type="file"
                    accept="image/*"
                    onChange={onImageFile}
                    className="hidden"
                  />
                </label>
                {imageCompressing && (
                  <span className="text-xs text-muted-foreground">{t('common.compressing')}</span>
                )}
                {imageFile && !imageCompressing && (
                  <button
                    type="button"
                    onClick={() => { setImageFile(null); setImagePreview(null); }}
                    className="text-xs text-red-500 hover:underline"
                  >
                    {t('common.remove')}
                  </button>
                )}
              </div>
            </div>

            {error && (
              <div className="mt-3 p-2 bg-destructive/10 border border-destructive/30 text-destructive rounded text-sm">{error}</div>
            )}

            <div className="mt-4 flex gap-2">
              <button
                onClick={submit}
                disabled={submitting || imageCompressing}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {submitting ? t('common.saving') : t('common.save')}
              </button>
              <button
                onClick={() => { setShowAdd(false); resetForm(); }}
                className="px-4 py-2 border border-input rounded-lg hover:bg-background"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'records' && showBulk && (
          <div className="bg-card border border-border rounded-xl p-5 shadow-sm mb-4">
            <h2 className="font-semibold text-foreground mb-1">{t('civil.stolenVehicles.bulkTitle')}</h2>
            <p className="text-xs text-muted-foreground mb-3">
              <Trans i18nKey="sv.bulk.paste" values={{ ext: '.json' }} components={{ code: <code /> }} />
              <Trans i18nKey="sv.bulk.eachItem" values={{ schema: '{ license_plate, country, make?, model?, color?, year?, reporting_station? }' }} components={{ code: <code /> }} />
            </p>
            <textarea
              value={bulkText}
              onChange={e => setBulkText(e.target.value)}
              placeholder={`[\n  { "license_plate": "1234ABC", "country": "ES", "make": "Seat" },\n  ...\n]`}
              className="w-full h-40 px-3 py-2 border border-input rounded text-xs font-mono focus:outline-none focus:border-primary"
            />
            <div className="mt-2 flex gap-2 items-center">
              <button
                onClick={() => bulkFileRef.current?.click()}
                className="flex items-center gap-1 px-3 py-1.5 border border-input rounded text-sm hover:bg-background"
              >
                <Upload className="w-4 h-4" /> {t('sv.bulk.loadFromFile')}
              </button>
              <button
                onClick={submitBulk}
                disabled={bulkSubmitting || !bulkText.trim()}
                className="px-4 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50"
              >
                {bulkSubmitting ? t('sv.uploading') : t('sv.upload')}
              </button>
            </div>

            {bulkResult && (
              <div className="mt-3 p-3 bg-background border border-border rounded text-sm">
                <Trans i18nKey="sv.bulk.result" values={{ inserted: bulkResult.inserted, failed: bulkResult.failed }} components={{ strong: <strong /> }} />
                {bulkResult.errors.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-muted-foreground cursor-pointer">{t('civil.common.viewErrors')}</summary>
                    <pre className="mt-2 text-xs bg-card p-2 rounded border border-border overflow-x-auto">
                      {JSON.stringify(bulkResult.errors, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            )}

            {error && (
              <div className="mt-3 p-2 bg-destructive/10 border border-destructive/30 text-destructive rounded text-sm">{error}</div>
            )}
          </div>
        )}

        {activeTab === 'records' && (
          <>
        {loading ? (
          <div className="text-muted-foreground text-sm py-8 text-center">{t('common.loading')}</div>
        ) : list.length === 0 ? (
          <div className="text-muted-foreground text-sm py-8 text-center">{t('records.empty.stolenVehicles')}</div>
        ) : (
          <>
            {/* v0.4.9.2.3c22ca: search + date-range filter strip */}
            <FiltersBar
              searchValue={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search by plate, make, model, color, country…"
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
            />
            {filteredList.length === 0 ? (
              <div className="text-muted-foreground text-sm py-8 text-center">
                {t('records.empty.noMatches')}
              </div>
            ) : (
            <div className="space-y-2">
              {filteredList.map((v) => (
              // v0.4.9.2.3c22b: added `gap-3` and a 48x48 thumbnail on the
              // left. Replaced `justify-between` with a `flex-1` title block
              // so the thumbnail + title sit together on the left, the
              // action buttons sit together on the right.
              <div key={v.id} className="bg-card border border-border rounded-lg p-3 shadow-sm flex items-center gap-3">
                {v.photo_url && (
                  <img
                    src={v.photo_url}
                    alt={`Vehicle ${v.license_plate}`}
                    className="w-12 h-12 object-cover rounded shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-mono font-semibold text-foreground">{v.license_plate}</div>
                  <div className="text-xs text-muted-foreground">
                    {v.country}
                    {v.make ? ` · ${v.make}` : ''}
                    {v.model ? ` ${v.model}` : ''}
                    {v.color ? ` · ${v.color}` : ''}
                    {v.year ? ` · ${v.year}` : ''}
                    {v.status !== 'active' ? ` · ${v.status}` : ''}
                  </div>
                </div>
                {/* v0.4.9.2.3c22a: wrap Pencil + Trash in a flex container so they
                    align right with a proper gap (was CROOKED before — both
                    buttons were siblings of the title block under
                    justify-between, so they ended up middle + right with no
                    gap between them). Matches Most Wanted / Missing Persons. */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => startEdit(v)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    title={t('records.title.editEntry')}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteVehicle(v.id)}
                    className="p-2 text-red-500 hover:bg-destructive/10 rounded"
                    title={t('common.delete')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
            )}
          </>
        )}
          </>
        )}

        {activeTab === 'bolo' && isAgentAdmin && (
          <BoloTab
            targetType={TAB_TARGET_TYPE}
            orgId={currentOrgId ?? undefined}
            availableOrgs={user?.role === 'superadmin' ? availableOrgs : undefined}
            records={list.map((r) => ({
              id: r.id,
              label: `${r.license_plate}${r.make || r.model ? ` (${[r.make, r.model].filter(Boolean).join(' ')})` : ''}`,
              country: r.country,
              photoUrl: null,
            }))}
          />
        )}
      </main>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-2xl p-5 my-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-foreground">{t('civil.stolenVehicles.editTitle')}</h2>
              {/* v0.4.9.2.3c22b: use closeEdit() so the image preview/file are
                  cleared along with the editing state. setEditing(null)
                  alone would leave editImage* dangling. */}
              <button onClick={closeEdit} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label={t('sv.field.plate')} value={editForm.license_plate} onChange={v => setEditForm({ ...editForm, license_plate: v.toUpperCase() })} />
              {/* v0.4.9.2.3c22a: strict alpha-3 country dropdown for edit. */}
              <SearchableSelect
                label={t('records.field.country')}
                value={editForm.country}
                onChange={v => setEditForm({ ...editForm, country: v })}
                options={countryListToOptions(COUNTRIES)}
                placeholder={t('records.field.searchCountry')}
                required
              />
              <Input label={t('sv.field.make')} value={editForm.make} onChange={v => setEditForm({ ...editForm, make: v })} />
              <Input label={t('sv.field.model')} value={editForm.model} onChange={v => setEditForm({ ...editForm, model: v })} />
              <Input label={t('sv.field.color')} value={editForm.color} onChange={v => setEditForm({ ...editForm, color: v })} />
              <Input label={t('sv.field.year')} type="number" value={editForm.year} onChange={v => setEditForm({ ...editForm, year: v })} />
              <Input label={t('records.field.reportingStation')} value={editForm.reporting_station} onChange={v => setEditForm({ ...editForm, reporting_station: v })} className="md:col-span-2" />
              <div className="md:col-span-2">
                <label className="text-xs text-muted-foreground uppercase tracking-wide">{t('civil.common.status')}</label>
                <select
                  value={editForm.status}
                  onChange={e => setEditForm({ ...editForm, status: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-input rounded text-sm bg-background"
                >
                  <option value="active">active</option>
                  <option value="recovered">recovered</option>
                  <option value="cancelled">cancelled</option>
                </select>
              </div>
            </div>

            {/* v0.4.9.2.3c22b: edit-image panel. Shows the existing photo (or
                a "no image" placeholder) until the user picks a replacement.
                If they don't pick, the server keeps the existing photo_url. */}
            <div className="mt-5">
              <label className="text-xs text-muted-foreground uppercase tracking-wide">
                {t('sv.image.title')}
              </label>
              <div className="mt-2 flex items-center gap-3">
                {editImagePreview ? (
                  <img
                    src={editImagePreview}
                    alt={t('sv.alt.vehiclePreview')}
                    className="w-20 h-20 object-cover rounded border border-border"
                  />
                ) : (
                  <div className="w-20 h-20 bg-accent rounded flex items-center justify-center text-xs text-muted-foreground">
                    {t('records.face.noImage')}
                  </div>
                )}
                <label className="cursor-pointer px-3 py-2 border border-input rounded text-sm hover:bg-background">
                  {editImageFile ? t('records.face.change') : t('sv.image.replace')}
                  <input
                    ref={editImageFileRef}
                    type="file"
                    accept="image/*"
                    onChange={onEditImageFile}
                    className="hidden"
                  />
                </label>
                {editImageCompressing && (
                  <span className="text-xs text-muted-foreground">{t('common.compressing')}</span>
                )}
                {editImageFile && !editImageCompressing && (
                  <button
                    type="button"
                    onClick={() => {
                      // Revert to the original photo URL (if any).
                      setEditImageFile(null);
                      setEditImagePreview(editing?.photo_url ?? null);
                    }}
                    className="text-xs text-red-500 hover:underline"
                  >
                    {t('common.revert')}
                  </button>
                )}
              </div>
              {!editImageFile && (
                <p className="text-xs text-muted-foreground mt-2">
                  {t('sv.image.replaceHint')}
                </p>
              )}
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button
                onClick={closeEdit}
                className="px-4 py-2 border border-input rounded-lg hover:bg-accent text-sm"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={submitEdit}
                disabled={editSubmitting || editImageCompressing}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 text-sm"
              >
                {editSubmitting ? t('common.saving') : t('records.action.saveChanges')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, type = 'text', className = '' }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; className?: string;
}) {
  return (
    <div className={className}>
      <label className="text-xs text-muted-foreground uppercase tracking-wide">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="mt-1 block w-full px-3 py-2 border border-input rounded text-sm focus:outline-none focus:border-primary"
      />
    </div>
  );
}
